import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Box,
  Typography,
  TablePagination,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, CheckCircle, Cancel, HourglassEmpty } from '@mui/icons-material';
import { getRebalance } from '../../Redux/Actions/transactionService';
import { useDispatch, useSelector } from 'react-redux';

function processData(orders) {
  const strategies = {};

  // Filter only rebalance orders first
  const rebalanceOrders = orders.filter(order => order.action === 'REBALANCE');

  rebalanceOrders.forEach((order) => {
    const { strategy, createdAt, type, status, amount } = order;
    const strategyName = strategy?.name || '-';

    if (!strategies[strategyName]) {
      strategies[strategyName] = {
        strategyName,
        type: type || 'N/A',
        latestRebalance: createdAt,
        orders: []
      };
    }

    // Update latest rebalance date if current order is newer
    if (new Date(createdAt) > new Date(strategies[strategyName].latestRebalance)) {
      strategies[strategyName].latestRebalance = createdAt;
    }

    // Add order details
    strategies[strategyName].orders.push({
      id: order.id,
      createdAt,
      status,
      amount: amount || 0,
      type,
      userEmail: order.userRole?.user?.email || 'N/A'
    });
  });

  // Convert the strategies object to an array and sort by latest rebalance date
  return Object.values(strategies).sort((a, b) => 
    new Date(b.latestRebalance) - new Date(a.latestRebalance)
  );
}

function Row({ row }) {
  const [open, setOpen] = useState(false);

  const sortedOrders = [...row.orders].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {row.strategyName}
        </TableCell>
        <TableCell>{row.type}</TableCell>
        <TableCell>{new Date(row.latestRebalance).toLocaleString()}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Typography variant="h6" gutterBottom component="div">
                Investor Details
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedOrders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>{order.userEmail}</TableCell>
                      <TableCell>{order.amount.toFixed(2)}</TableCell>
                      <TableCell>{new Date(order.createdAt).toLocaleString()}</TableCell>
                      <TableCell>
                        {order.status === 'COMPLETED' && (
                          <span style={{ color: 'green', display: 'flex', alignItems: 'center' }}>
                            <CheckCircle style={{ marginRight: '4px' }} />
                            Completed
                          </span>
                        )}
                        {order.status === 'FAILED' && (
                          <span style={{ color: 'red', display: 'flex', alignItems: 'center' }}>
                            <Cancel style={{ marginRight: '4px' }} />
                            Failed
                          </span>
                        )}
                        {order.status === 'PENDING' && (
                          <span style={{ color: 'orange', display: 'flex', alignItems: 'center' }}>
                            <HourglassEmpty style={{ marginRight: '4px' }} />
                            Pending
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function ExpandableTable() {
  const dispatch = useDispatch();
  const { orders } = useSelector((state) => state.transaction);
  const userInfoString = localStorage.getItem("userInfo");
  const userInfo = userInfoString && userInfoString !== "undefined" ? JSON.parse(userInfoString) : null;
  const role = userInfo?.portal || 'MANAGER';
  const entityId= localStorage.getItem('entId')

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    dispatch(getRebalance({}));
  }, [dispatch]);

  const processedData = processData(orders);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page after changing rows per page
  };

  return (
    <TableContainer component={Paper} style={{ padding: '10px' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>Strategy Name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Latest Rebalance</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {processedData
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((row) => (
              <Row key={row.strategyName} row={row} />
            ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={processedData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </TableContainer>
  );
}
